import React, { useState, useMemo, useCallback, useRef } from 'react';
import _ from 'lodash';
import {
  useSubmissionsStats,
  useCommentBanksStats,
  useSuccessCriteriaStats,
  useRubricsStats,
  useShortAnswerStats,
} from '../state/hooks';
import { PageHeader } from './PageHeader';
import { FilterHeader } from './FilterHeader';
import DropdownMenu from '../DropdownMenu';
import DateSelector from '../DateSelector';
import dayjs from 'dayjs';
import { UsersIcon, FilterIcon, ChevronDown } from './Icons';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { ClassChangeRequestDialog } from '../Shared/ClassChangeRequestDialog';
import { useClassInsights } from './ClassInsightsContext';
import { statsBody, withDateDefaults } from './useFilters';
import {
  deriveClassOptions,
  deriveSubjectOptions,
  deriveTaskTypeOptions,
  deriveTaskOptions,
  deriveStudentOptions,
  selectedClasses,
  selectedSubjects,
  selectedTaskTypes,
  markingToolsInScope,
  catalogClasses,
} from './catalogSelectors';
import { ALL_OPTION } from '../../utils/urlParams';
import textPdfIcon from '../../static/icons/text-pdf-icon.svg';

const isShowAll = (ids) => Array.isArray(ids) && ids.length === 1 && ids[0] === ALL_OPTION;
import classListIcon from '../../static/icons/class-list-icon.svg';
import classChangeIcon from '../../static/icons/class-change-icon.svg';
import classOptionIcon from '../../static/icons/class-option-icon.svg';
import { ClassListPopup } from './ClassListPopup';

export function Header() {
  const { filters, debouncedFilters, setFilters, catalog } = useClassInsights();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [hasToggledFilters, setHasToggledFilters] = useState(false);
  const [classOptionsOpen, setClassOptionsOpen] = useState(false);
  const [classListPopupOpen, setClassListPopupOpen] = useState(false);
  const [classChangeRequestOpen, setClassChangeRequestOpen] = useState(false);
  // The menu item that opens the form is gone by the time it closes, so focus
  // returns to the menu's trigger instead.
  const classOptionsButtonRef = useRef(null);
  // JED-1066: enable stats whenever a class scope exists (catalog has classes);
  // empty classIds means "all classes" and must still fetch.
  const hasClassScope = catalogClasses(catalog).length > 0;
  const { data: submissionsData } = useSubmissionsStats(
    statsBody(debouncedFilters),
    hasClassScope
  );

  const baseExportFilters = useMemo(() => statsBody(debouncedFilters), [debouncedFilters]);

  const { data: commentBankStatsData, isLoading: commentBanksLoading } = useCommentBanksStats(
    baseExportFilters,
    hasClassScope
  );

  const { data: successCriteriaStatsData, isLoading: successCriteriaLoading } = useSuccessCriteriaStats(
    baseExportFilters,
    hasClassScope
  );

  const { data: rubricsStatsData, isLoading: rubricsLoading } = useRubricsStats(
    baseExportFilters,
    hasClassScope
  );

  const { data: shortAnswerStatsData, isLoading: shortAnswerLoading } = useShortAnswerStats(
    baseExportFilters,
    hasClassScope
  );

  const getMarkingToolIdsForExport = (filterIds, dataOptions) => {
    if (isShowAll(filterIds)) return null;

    if (filterIds && filterIds.length > 0) {
      return filterIds;
    }

    if (dataOptions && dataOptions.length > 0) {
      return [String(dataOptions[0].id)];
    }

    return null;
  };

  const commentBanksExportIds = useMemo(() =>
    getMarkingToolIdsForExport(debouncedFilters.commentBankIds, commentBankStatsData?.options),
    [debouncedFilters.commentBankIds, commentBankStatsData?.options]
  );

  const successCriteriaExportIds = useMemo(() =>
    getMarkingToolIdsForExport(debouncedFilters.successCriteriaIds, successCriteriaStatsData?.options),
    [debouncedFilters.successCriteriaIds, successCriteriaStatsData?.options]
  );

  const rubricsExportIds = useMemo(() =>
    getMarkingToolIdsForExport(debouncedFilters.rubricIds, rubricsStatsData?.options),
    [debouncedFilters.rubricIds, rubricsStatsData?.options]
  );

  const shortAnswerExportIds = useMemo(() =>
    getMarkingToolIdsForExport(debouncedFilters.shortAnswerIds, shortAnswerStatsData?.options),
    [debouncedFilters.shortAnswerIds, shortAnswerStatsData?.options]
  );

  // JED-1066: an empty class selection means "all classes" — the stats query is
  // enabled (hasClassScope) and the backend returns all-class data in that
  // state. Scope the dependent filter dropdowns, counts and tool validation to
  // ALL catalog classes too, so they don't render empty while the stats are
  // populated. (selectedClasses intentionally returns [] for empty classIds, so
  // we widen here rather than changing that shared selector.)
  const allClassIds = catalogClasses(catalog).map((c) => String(c.id));
  const scopeFilters =
    !filters.classIds || filters.classIds.length === 0
      ? { ...filters, classIds: allClassIds }
      : filters;

  const classes = selectedClasses(catalog, scopeFilters);
  const subjects = selectedSubjects(classes, scopeFilters);
  const taskTypes = selectedTaskTypes(subjects, scopeFilters);

  const classOptions = deriveClassOptions(catalog);
  const subjectOptions = deriveSubjectOptions(classes);
  const taskTypeOptions = deriveTaskTypeOptions(subjects);
  const taskOptions = deriveTaskOptions(taskTypes);
  const studentOptions = deriveStudentOptions(classes);

  const dates = withDateDefaults(filters);

  const validateMarkingToolIds = (newFilters) => {
    // JED-1066: empty classIds means "all classes", so resolve tools against all
    // catalog classes — otherwise selecting "all" would clear the user's
    // scoped tool filters (markingToolsInScope -> selectedClasses([]) is empty).
    const scopedNewFilters =
      !newFilters.classIds || newFilters.classIds.length === 0
        ? { ...newFilters, classIds: catalogClasses(catalog).map((c) => String(c.id)) }
        : newFilters;
    const toolsInScope = markingToolsInScope(catalog, scopedNewFilters);

    return {
      commentBankIds: (filters.commentBankIds || []).filter((id) =>
        toolsInScope.commentBanks.some((opt) => String(opt.id) === String(id))
      ),
      successCriteriaIds: isShowAll(filters.successCriteriaIds)
        ? filters.successCriteriaIds
        : (filters.successCriteriaIds || []).filter((id) =>
            toolsInScope.successCriteria.some((opt) => String(opt.id) === String(id))
          ),
      rubricIds: (filters.rubricIds || []).filter((id) =>
        toolsInScope.rubrics.some((opt) => String(opt.id) === String(id))
      ),
      shortAnswerIds: (filters.shortAnswerIds || []).filter((id) =>
        toolsInScope.shortAnswers.some((opt) => String(opt.id) === String(id))
      ),
    };
  };

  const handleClassChange = (values) => {
    const classOptions = deriveClassOptions(catalog);
    // Don't auto-convert empty to "all" - let users deselect everything
    const validClassIds = values.filter((id) =>
      classOptions.some((opt) => String(opt.id) === String(id))
    );
    // JED-1066: empty classIds means "all classes". Validate the dependent
    // subject/task/student selections against ALL catalog classes in that state
    // so selecting "All" doesn't drop otherwise-valid filters (the actual
    // selection stored in newFilters.classIds stays empty = all).
    const scopeClassIds = validClassIds.length === 0
      ? classOptions.map((opt) => String(opt.id))
      : validClassIds;

    const classes = selectedClasses(catalog, { ...filters, classIds: scopeClassIds });
    const subjectOptions = deriveSubjectOptions(classes);
    const studentOptions = deriveStudentOptions(classes);

    const validSubjectCodes = filters.subjectCodes.filter((code) =>
      subjectOptions.some((opt) => String(opt.id) === String(code))
    );

    const subjects = selectedSubjects(classes, { ...filters, classIds: scopeClassIds, subjectCodes: validSubjectCodes });
    const taskTypeOptions = deriveTaskTypeOptions(subjects);

    const validTaskTypes = filters.taskTypes.filter((type) =>
      taskTypeOptions.some((opt) => String(opt.id) === String(type))
    );

    const taskTypes = selectedTaskTypes(subjects, { ...filters, classIds: scopeClassIds, subjectCodes: validSubjectCodes, taskTypes: validTaskTypes });
    const taskOptions = deriveTaskOptions(taskTypes);

    const validTaskIds = filters.taskIds.filter((id) =>
      taskOptions.some((opt) => String(opt.id) === String(id))
    );

    const validStudentIds = filters.studentIds.filter((id) =>
      studentOptions.some((opt) => String(opt.id) === String(id))
    );

    const newFilters = {
      classIds: validClassIds,
      subjectCodes: validSubjectCodes,
      taskTypes: validTaskTypes,
      taskIds: validTaskIds,
      studentIds: validStudentIds,
    };

    setFilters({
      ...newFilters,
      ...validateMarkingToolIds(newFilters),
    });
  };

  const handleToggleFilters = () => {
    setHasToggledFilters(true);
    setFiltersVisible((v) => !v);
  };

  const dropdownUiProps = {
    triggerFontSize: '16px',
    triggerPadding: '10px 16px',
  };

  const availableStudentIds = new Set(
    studentOptions.map((student) => String(student.id))
  );
  // JED-959: the chip counts the people actually IN the class. Past students are
  // in studentOptions so they stay filterable, but counting them here would read
  // as a roster of 9 against a popup showing "8 students +1 past" — the opposite
  // of the clarity the past/current split is for. Once the teacher has filtered
  // to specific students, the count follows their selection, past ones included.
  const currentStudentIds = new Set(
    studentOptions.filter((student) => !student.isFormer).map((student) => String(student.id))
  );
  const studentCount = filters.studentIds.length === 0
    ? currentStudentIds.size
    : filters.studentIds.filter((id) => availableStudentIds.has(String(id))).length;

  const exportFilterData = useMemo(() => {
    const isDataLoading = commentBanksLoading || successCriteriaLoading || rubricsLoading || shortAnswerLoading;

    const filterByMarkingToolIds = (stats, markingToolIds, dataType) => {
      if (!stats || !Array.isArray(stats)) return stats;
      if (!markingToolIds || markingToolIds.length === 0) return stats;

      return stats.filter(item =>
        markingToolIds.some(id => String(item.id) === String(id))
      );
    };

    const filterRubricsByIds = (rubricsStats, markingToolIds) => {
      if (!rubricsStats || !Array.isArray(rubricsStats)) return rubricsStats;
      if (!markingToolIds || markingToolIds.length === 0) return rubricsStats;

      return rubricsStats
        .map(task => {
          const filteredStats = (task.stats || []).filter(stat =>
            markingToolIds.some(id => String(stat.rubricId) === String(id))
          );

          if (filteredStats.length > 0) {
            return { ...task, stats: filteredStats };
          }
          return null;
        })
        .filter(Boolean);
    };

    const filteredCommentBankStats = filterByMarkingToolIds(
      commentBankStatsData?.stats,
      commentBanksExportIds,
      'Comment Banks'
    );

    const filteredSuccessCriteriaStats = filterByMarkingToolIds(
      successCriteriaStatsData?.stats,
      successCriteriaExportIds,
      'Success Criteria'
    );

    const filteredRubricsStats = filterRubricsByIds(
      rubricsStatsData?.stats,
      rubricsExportIds
    );

    const filteredShortAnswerStats = shortAnswerStatsData?.stats;

    const classLabels = filters.classIds
      .map(id => classOptions.find(opt => String(opt.id) === String(id))?.label)
      .filter(Boolean);

    const dateRange = dates.startDate && dates.endDate
      ? `${dates.startDate} to ${dates.endDate}`
      : null;

    const commentBankLabels = (commentBankStatsData?.options || [])
      .filter((bank) => (filters.commentBankIds || []).includes(String(bank.id)))
      .map((bank) => bank.title || bank.name)
      .filter(Boolean);

    const successCriteriaLabels = (successCriteriaStatsData?.options || [])
      .filter((sc) => (filters.successCriteriaIds || []).includes(String(sc.id)))
      .map((sc) => sc.title || sc.name)
      .filter(Boolean);

    const rubricLabels = (rubricsStatsData?.options || [])
      .filter((rubric) => (filters.rubricIds || []).includes(String(rubric.id)))
      .map((rubric) => rubric.title || rubric.name)
      .filter(Boolean);

    const shortAnswerLabels = (shortAnswerStatsData?.options || [])
      .filter((sa) => (filters.shortAnswerIds || []).includes(String(sa.id)))
      .map((sa) => sa.title || sa.name || sa.question)
      .filter(Boolean);

    const totalSuccessCriteriaObjectives = (filteredSuccessCriteriaStats || []).reduce(
      (sum, sc) => sum + (sc.objectives?.length || 0), 0
    );
    const totalRubricRows = (filteredRubricsStats || []).reduce(
      (sum, task) => sum + (task.stats?.length || 0), 0
    );
    const totalShortAnswerEntries = (filteredShortAnswerStats?.tasks || []).reduce(
      (sum, task) => sum + (task.studentStats?.reduce((s, st) => s + (st.entries?.length || 0), 0) || 0), 0
    );


    return {
      submissionsStats: submissionsData,
      commentBankStats: filteredCommentBankStats,
      commentBankOptions: commentBankStatsData?.options,
      successCriteriaStats: filteredSuccessCriteriaStats,
      successCriteriaOptions: successCriteriaStatsData?.options,
      rubricsStats: filteredRubricsStats,
      rubricsOptions: rubricsStatsData?.options,
      shortAnswerStats: filteredShortAnswerStats,
      shortAnswerOptions: shortAnswerStatsData?.options,
      classIds: filters.classIds,
      subjectCodes: filters.subjectCodes,
      taskTypes: filters.taskTypes,
      taskIds: filters.taskIds,
      studentIds: filters.studentIds,
      startDate: dates.startDate,
      endDate: dates.endDate,
      selectedCommentBankIds: filters.commentBankIds || [],
      selectedRubricIds: filters.rubricIds || [],
      selectedShortAnswerIds: filters.shortAnswerIds || [],
      selectedSuccessCriteriaIds: filters.successCriteriaIds || [],

      classLabels,
      dateRange,
      commentBankLabels,
      successCriteriaLabels,
      rubricLabels,
      shortAnswerLabels,
      filters: {
        subjects: filters.subjectCodes,
        taskTypes: filters.taskTypes,
        taskIds: filters.taskIds?.map(id => taskOptions.find(opt => String(opt.id) === String(id))?.label).filter(Boolean),
        studentIds: filters.studentIds?.map(id => studentOptions.find(opt => String(opt.id) === String(id))?.label).filter(Boolean),
      },
      isDataLoading,
    };
  }, [
    submissionsData,
    commentBankStatsData,
    successCriteriaStatsData,
    rubricsStatsData,
    shortAnswerStatsData,
    filters,
    classOptions,
    dates,
    taskOptions,
    studentOptions,
    commentBanksLoading,
    successCriteriaLoading,
    rubricsLoading,
    shortAnswerLoading,
    commentBanksExportIds,
    successCriteriaExportIds,
    rubricsExportIds,
    shortAnswerExportIds,
  ]);


  return (
    <>
      <PageHeader timeSavedHours={submissionsData?.timeSavedHours ?? 0}>
        <div className="ci-heading-class-multiselect">
          <DropdownMenu
            multiselect
            options={classOptions}
            selectedValues={filters.classIds}
            onChange={handleClassChange}
            placeholder="Select classes"
            getOptionLabel={(opt) => {
              if (typeof opt === 'object' && opt.label) return opt.label;
              return classOptions.find((x) => String(x.id) === String(opt))?.label ?? '';
            }}
            getOptionValue={(opt) => {
              if (typeof opt === 'object' && opt.id) return opt.id;
              return opt;
            }}
            alwaysWhite
            showSearch
            showCheckboxes
            showAllOption
            compactDisplay
            listAllWhenEmpty
            groupByArchived
            activeLabel="Active Classes"
            archivedLabel="Archived Classes"
            allLabel="All Classes"
            {...dropdownUiProps}
            minDropdownWidth="320px"
          />
        </div>

        <button type="button" className="ci-chip" onClick={() => window.dispatchEvent(new Event('openStudentsPopup'))}>
          <UsersIcon />
          <span>{studentCount} {studentCount === 1 ? 'student' : 'students'}</span>
        </button>

        <button
          type="button"
          className={`ci-chip ci-chip-filter ${filtersVisible ? 'ci-chip--active' : ''}`.trim()}
          onClick={handleToggleFilters}
        >
          <FilterIcon />
          <span data-dd-privacy="mask-user-input">Filters</span>
        </button>

        <div className="ci-class-options-wrap">
          <button
            type="button"
            className="ci-chip"
            ref={classOptionsButtonRef}
            onClick={() => setClassOptionsOpen((v) => !v)}
          >
            <img src={classOptionIcon} alt="" className="ci-chip-icon" /> Class Options <ChevronDown />
          </button>

          {classOptionsOpen && (
            <>
              <button
                type="button"
                className="ci-class-options-backdrop"
                aria-label="Close class options"
                onClick={() => setClassOptionsOpen(false)}
              />
              <div className="ci-class-options-dropdown" role="menu">
                <div className="ci-class-options-dropdown-header">Class Options</div>
                <ExportFormatDropdown
                  isFullReport={true}
                  allData={exportFilterData}
                  filterData={exportFilterData}
                  trigger={
                    <button type="button" className="ci-class-options-option">
                      <img src={textPdfIcon} alt="" className="ci-class-options-option-icon-img" />
                      Generate class report
                    </button>
                  }
                />
                <button
                  type="button"
                  className="ci-class-options-option"
                  onClick={() => {
                    setClassOptionsOpen(false);
                    setClassListPopupOpen(true);
                  }}
                >
                  <img src={classListIcon} alt="" className="ci-class-options-option-icon-img" />
                  View class list
                </button>
                <button
                  type="button"
                  className="ci-class-options-option"
                  onClick={() => {
                    setClassOptionsOpen(false);
                    setClassChangeRequestOpen(true);
                  }}
                >
                  <img src={classChangeIcon} alt="" className="ci-class-options-option-icon-img" />
                  Request class change
                </button>
              </div>
            </>
          )}
        </div>
      </PageHeader>

      <div
        className={`ci-page-top-filters-wrapper ${
          hasToggledFilters ? (filtersVisible ? 'ci-filters-visible' : 'ci-filters-hidden') : ''
        }`}
      >
        {filtersVisible && (
          <FilterHeader className="ci-page-top-filters ci-comment-bank-modal-filters">
            <div className="ci-modal-filter-group ci-top-filter-multiselect">
              <label>Classes</label>
              <DropdownMenu
                multiselect
                options={classOptions}
                selectedValues={filters.classIds}
                onChange={handleClassChange}
                placeholder="All classes"
                getOptionLabel={(opt) => {
                  if (typeof opt === 'object' && opt.label) return opt.label;
                  return classOptions.find((x) => String(x.id) === String(opt))?.label ?? '';
                }}
                getOptionValue={(opt) => {
                  if (typeof opt === 'object' && opt.id) return opt.id;
                  return opt;
                }}
                alwaysWhite
                showSearch
                showCheckboxes
                showAllOption
                compactDisplay
                listAllWhenEmpty
                groupByArchived
                activeLabel="Active Classes"
                archivedLabel="Archived Classes"
                allLabel="All Classes"
                {...dropdownUiProps}
                dropdownHeader="Classes"
                dropdownSubtitle="Select classes to show data for"
              />
            </div>
            {subjectOptions.length > 1 && (
              <div className="ci-modal-filter-group ci-top-filter-multiselect">
                <label>Subject</label>
                <DropdownMenu
                  multiselect
                  options={subjectOptions}
                  selectedValues={filters.subjectCodes}
                  onChange={(values) => {
                    if (_.isEqual(values, filters.subjectCodes)) return;
                    const newFilters = { ...filters, subjectCodes: values, taskTypes: [], taskIds: [] };
                    setFilters({
                      ...newFilters,
                      ...validateMarkingToolIds(newFilters),
                    });
                  }}
                  placeholder="All subjects"
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.id}
                  alwaysWhite
                  showSearch
                  showCheckboxes
                  showAllOption
                  compactDisplay
                  {...dropdownUiProps}
                  dropdownHeader="Subject"
                  dropdownSubtitle="Select subjects to show data for"
                />
              </div>
            )}
            <div className="ci-modal-filter-group ci-top-filter-multiselect">
              <label>Task type</label>
              <DropdownMenu
                multiselect
                options={taskTypeOptions}
                selectedValues={filters.taskTypes}
                onChange={(values) => {
                  if (_.isEqual(values, filters.taskTypes)) return;
                  const newFilters = { ...filters, taskTypes: values, taskIds: [] };
                  setFilters({
                    ...newFilters,
                    ...validateMarkingToolIds(newFilters),
                  });
                }}
                placeholder="All task types"
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.id}
                alwaysWhite
                showSearch
                showCheckboxes
                showAllOption
                compactDisplay
                {...dropdownUiProps}
                dropdownHeader="Task type"
                dropdownSubtitle="Select task types to show data for"
              />
            </div>
            <div className="ci-modal-filter-group ci-top-filter-multiselect">
              <label>Task name</label>
              <DropdownMenu
                multiselect
                options={taskOptions}
                selectedValues={filters.taskIds}
                onChange={(values) => {
                  if (_.isEqual(values, filters.taskIds)) return;
                  const newFilters = { ...filters, taskIds: values };
                  setFilters({
                    ...newFilters,
                    ...validateMarkingToolIds(newFilters),
                  });
                }}
                placeholder="All tasks"
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.id}
                alwaysWhite
                showSearch
                showCheckboxes
                showAllOption
                compactDisplay
                {...dropdownUiProps}
                dropdownHeader="Task name"
                dropdownSubtitle="Select tasks to show data for"
              />
            </div>
            <div className="ci-modal-filter-group ci-top-filter-multiselect">
              <label>Students</label>
              <DropdownMenu
                multiselect
                options={studentOptions}
                selectedValues={filters.studentIds}
                onChange={(values) => setFilters({ studentIds: values })}
                placeholder="All students"
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.id}
                alwaysWhite
                showSearch
                showCheckboxes
                showAllOption
                compactDisplay
                {...dropdownUiProps}
                dropdownHeader="Students"
                dropdownSubtitle="Select students to show data for"
              />
            </div>
            <div className="ci-modal-filter-group">
              <label>From</label>
              <DateSelector
                value={dates.startDate ? dayjs(dates.startDate) : dayjs('2026-01-01')}
                onChange={(newValue) => setFilters({ startDate: newValue ? newValue.format('YYYY-MM-DD') : '2026-01-01' })}
                dateOnly
                readOnlyInput
                matchDropdownStyle
                minDate={null}
              />
            </div>
            <div className="ci-modal-filter-group">
              <label>To</label>
              <DateSelector
                value={dates.endDate ? dayjs(dates.endDate) : dayjs()}
                onChange={(newValue) => setFilters({ endDate: newValue ? newValue.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD') })}
                dateOnly
                readOnlyInput
                matchDropdownStyle
                minDate={null}
              />
            </div>
          </FilterHeader>
        )}
      </div>
      <ClassListPopup open={classListPopupOpen} onClose={() => setClassListPopupOpen(false)} />
      <ClassChangeRequestDialog
        open={classChangeRequestOpen}
        onClose={() => {
          setClassChangeRequestOpen(false);
          classOptionsButtonRef.current?.focus();
        }}
      />
    </>
  );
}
